/**
 * `SCR-BOOK` (14 states), `SCR-CLOTH` (12), `SCR-SWATCH` (6). FLOWS_SPEC §5.
 * OWNER: web-frontend-engineer.
 *
 * One grid, four filters, one sort, one curation section. Not tabs, not
 * collections, not a merchandised landing page. The cloth is the hero.
 *
 * PERMANENTLY ABSENT at any book size: ratings, reviews, wishlists, "recently
 * viewed", view counts, "trending", "popular", facet count badges, infinite
 * scroll without a stated total, sponsored or paid placement.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import { api } from '../lib/api.ts';
import { useResource, useSubmission } from '../lib/resource.ts';
import { commissionTypeLabel, occasionLabel } from '../lib/optionCopy.ts';
import { compositionLine, dateInWords, joinList, metres, money } from '@pt/view';
import { weaveLabel } from '../lib/label.ts';
import { bookPage, defaultShelf, shelfCounts, type BookShelf } from '../lib/shelves.ts';
import { AppShell } from '../app/AppShell.tsx';
import { Consultation } from '../components/consultation.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import {
  AvailabilityLine,
  BookPager,
  BookShelves,
  ColourStatement,
  MacroSwatch,
  SwatchBookGrid,
  bookCountLine,
  clothReasonText,
  shelfLabel,
} from '../components/cloth.tsx';
import { Button, FactLine, RovingRadioGroup, Statement, TextInput } from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';

const SEASONS = ['high_summer', 'spring_summer', 'year_round', 'autumn_winter', 'winter'] as const;

export interface BookFilters {
  season: string;
  occasion: string;
  weave: string;
  sort: string;
}

const EMPTY: BookFilters = { season: '', occasion: '', weave: '', sort: 'house' };

/**
 * `SCR-BOOK` and `SCR-CLOTHSTEP` render THE SAME GRID. This is that grid.
 *
 * ── C4, 5 SEPTEMBER 2026 (STYLIST-1 fix wave): THE BOOK OPENS ON A SHELF ──
 * (the ruling is in DESIGN_RULINGS.md under "STYLIST-1 FIX WAVE")
 *
 * Measured before: 1,798 cloths and 1,796 <img> on one page, 313,578 px tall;
 * the first viewport five columns of radio options; the swatch act repeated on
 * every card. The demo's book has never done this — it opens on navy and the
 * rest is a shelf away — so this screen now does what that one does:
 *
 *   · THE SHELVES LEAD. One row, the house order (lib/shelves.ts), the whole
 *     book last; the book opens on the first shelf that holds a cloth. The
 *     shelf is the colour dimension — the old colour facet, which matched a
 *     substring of the colour name and had no word for burgundy or statement,
 *     is gone.
 *   · THE FILTERS WAIT BEHIND ONE ACT ("Narrow the book"), so the first
 *     viewport is cloth. A filter that is on while the panel is folded is
 *     named in the act's own label — a set filter is never a hidden decision
 *     (ST-BOOK-INCONTEXT's rule, applied to filters he set himself).
 *   · TWENTY-FOUR TO A PAGE, with the count of the shelf above the page and the
 *     page range and page acts below it — a stated total, never an infinite
 *     scroll (FLOWS_SPEC §5.1). A page turn moves focus to the count line; a
 *     filter or shelf change resets to page one and scrolls nothing.
 *   · ONE ACT PER PAGE. The tile opens the cloth; the swatch is offered on the
 *     cloth's own page, and the book says once, above the grid, where to go.
 */
export function BookScreen({ draftId, inFlow = false }: { draftId?: string; inFlow?: boolean }) {
  const { navigate } = useNavigation();
  const [filters, setFilters] = useState<BookFilters>(EMPTY);
  const [revealed, setRevealed] = useState(false);
  /* The shelf he chose, or null while the book stands on its opening shelf. */
  const [chosenShelf, setChosenShelf] = useState<BookShelf | null>(null);
  const [page, setPage] = useState(1);
  const [narrowing, setNarrowing] = useState(false);
  const filtersId = useId();
  const countRef = useRef<HTMLParagraphElement>(null);
  const pageTurned = useRef(false);

  const book = useResource(
    () =>
      api.fabrics({
        season: filters.season,
        occasion: filters.occasion,
        weave: filters.weave,
        sort: filters.sort,
        draftId,
      }),
    [filters.season, filters.occasion, filters.weave, filters.sort, draftId],
  );

  /* A page turn moves focus to the count line above the new page — the
     accessible way to say "here is the next page". Nothing else here scrolls
     (COMPONENT_SPEC §4.2: never auto-scrolled; a reset is instant). */
  useEffect(() => {
    if (!pageTurned.current) return;
    pageTurned.current = false;
    countRef.current?.focus();
  }, [page]);

  const data = book.data;
  const typeLabel = data?.commissionTypeLabel ?? null;
  const offered = data?.cloths ?? [];
  const excluded = data?.excluded ?? [];
  const counts = shelfCounts(offered, excluded);
  const shelf: BookShelf = chosenShelf ?? defaultShelf(counts);
  const current = bookPage(offered, excluded, { shelf, page, revealed });

  const activeFilters = [
    filters.season === '' ? '' : tDynamic(`book.filter.season.${filters.season}`),
    filters.occasion === '' ? '' : occasionLabel(filters.occasion),
    filters.weave === '' ? '' : (weaveLabel(filters.weave) ?? filters.weave),
  ].filter((value) => value !== '');
  const filtersOn = activeFilters.length > 0;

  const changeFilters = (next: (f: BookFilters) => BookFilters) => {
    setFilters(next);
    setPage(1);
  };
  const chooseShelf = (next: BookShelf) => {
    setChosenShelf(next);
    setPage(1);
  };
  const turnPage = (next: number) => {
    pageTurned.current = true;
    setPage(next);
  };
  const clearFilters = () => changeFilters(() => ({ ...EMPTY, sort: filters.sort }));

  const body = (
    <>
      {/* ST-BOOK-PLACEHOLDER — RULING-009's standing statement, BENEATH THE
          BOOK TITLE, driven by data: flipping the seed flag changes it with no
          code edit. Not dismissible, no alert colour, no icon, no container. */}
      {data?.placeholderCatalogue === true ? <Statement>{t('book.placeholder.statement')}</Statement> : null}

      {book.phase === 'skeleton' || book.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="tile" slow={book.slow} label={t('book.heading')} />
      ) : null}
      {book.phase === 'error' ? <FetchError whatKey="book.error.what" onRetry={book.reload} /> : null}

      {data === null ? null : (
        <>
          {/* ST-BOOK-INCONTEXT: a pre-set filter is VISIBLY set, with the same
              affordance as one he set himself. A hidden pre-filter is a hidden
              decision. */}
          {data.commissionTypeLabel === null ? null : <FactLine>{t('book.incontext.preset')}</FactLine>}

          {/* THE SHELVES LEAD (C4). */}
          {/* STYLIST-2 (6 September 2026): the book LEADS with the consultation —
              three questions and three cloths with the house's working, from
              @pt/core's engine over the offered book. The answers rank the
              three and never filter the shelves below (law 33(a), LUX-R5(2)).
              It stands only when the book can give three (LUX-R3(b)). */}
          {offered.length >= 3 && !filtersOn ? (
            <Consultation
              cloths={offered}
              occasions={data.facets.occasion}
              commissionTypeLabel={typeLabel}
              onOpen={(clothId) => navigate(inFlow ? `/commission/cloth/${clothId}` : `/book/${clothId}`)}
            />
          ) : null}
          {counts.all === 0 ? null : <BookShelves counts={counts} chosen={shelf} onChoose={chooseShelf} />}

          {/* THE FILTERS, BEHIND ONE ACT. The label carries the state — and,
              folded with a filter on, the filter — so nothing about the
              narrowing in force lives only in `aria-expanded`. */}
          {counts.all === 0 && !filtersOn ? null : (
            <div className="pt-actions pt-noprint">
              <Button aria-expanded={narrowing} aria-controls={filtersId} onClick={() => setNarrowing((value) => !value)}>
                {narrowing
                  ? t('book.narrow.hide')
                  : filtersOn
                    ? t('book.narrow.on', { activeFiltersList: joinList(activeFilters) })
                    : t('book.narrow.show')}
              </Button>
            </div>
          )}
          <div id={filtersId} className="pt-filters pt-filters--narrow" hidden={!narrowing}>
            <FilterGroup
              legend={t('book.filter.season.label')}
              values={SEASONS.filter((value) => data.facets.seasonClass.includes(value))}
              labelOf={(value) => tDynamic(`book.filter.season.${value}`)}
              chosen={filters.season}
              onChoose={(value) => changeFilters((f) => ({ ...f, season: value }))}
            />
            <FilterGroup
              legend={t('book.filter.occasion.label')}
              values={data.facets.occasion}
              labelOf={occasionLabel}
              chosen={filters.occasion}
              onChoose={(value) => changeFilters((f) => ({ ...f, occasion: value }))}
            />
            <FilterGroup
              legend={t('book.filter.cloth.label')}
              values={data.facets.weave}
              labelOf={(value) => weaveLabel(value) ?? value}
              chosen={filters.weave}
              onChoose={(value) => changeFilters((f) => ({ ...f, weave: value }))}
            />
            {/* Price sorts in BOTH directions, equally easy. Rank is never sold. */}
            <FilterGroup
              legend={t('book.sort.label')}
              values={['house', 'weight', 'price_asc', 'price_desc']}
              labelOf={(value) =>
                value === 'house'
                  ? t('book.sort.house')
                  : value === 'weight'
                    ? t('book.sort.weight.asc')
                    : value === 'price_asc'
                      ? t('book.sort.price.asc')
                      : t('book.sort.price.desc')
              }
              chosen={filters.sort}
              onChoose={(value) => changeFilters((f) => ({ ...f, sort: value }))}
            />
          </div>

          {/* ST-BOOK-HOUSE renders only when ≥2 slots fill. A single
              recommendation reads as a push. Slot A is not built (§13.2). */}
          {data.curation.length >= 2 ? (
            <section className="pt-block">
              <h2 className="pt-record__groupHeading">{t('book.house.heading')}</h2>
              {data.curation.map((slot) => (
                <FactLine key={slot.id}>{slot.reasonText}</FactLine>
              ))}
            </section>
          ) : null}

          {current.offeredTotal === 0 && current.hiddenTotal === 0 ? (
            counts.all === 0 && !filtersOn ? (
              <Statement>{t('book.empty')}</Statement>
            ) : (
              /* ST-BOOK-FILTERED-EMPTY, said of the shelf he is on: the shelf's
                 word and the filters that are on, and one act to clear them. */
              <div>
                <Statement>
                  {t('book.filteredEmpty.statement', {
                    activeFiltersList: joinList(
                      shelf === 'all' ? activeFilters : [shelfLabel(shelf), ...activeFilters],
                    ),
                  })}
                </Statement>
                {filtersOn ? (
                  <div className="pt-actions">
                    <Button onClick={clearFilters}>{t('global.action.clearFilters')}</Button>
                  </div>
                ) : null}
              </div>
            )
          ) : (
            <>
              {/* The count of the shelf, whole — what the page is a page OF —
                  and the one line that says where the act went. Focus lands
                  here on a page turn. */}
              {current.offeredTotal === 0 ? null : (
                <p className="pt-fact pt-book__count" tabIndex={-1} ref={countRef}>
                  {bookCountLine(shelf, current.offeredTotal)}
                </p>
              )}
              <FactLine>{t('book.open.hint')}</FactLine>
              <SwatchBookGrid
                cloths={current.rows}
                hidden={current.hiddenRows}
                revealed={revealed}
                onToggleReveal={() => setRevealed((value) => !value)}
                commissionTypeLabel={typeLabel}
                onOpen={(clothId) =>
                  navigate(inFlow ? `/commission/cloth/${clothId}` : `/book/${clothId}`)
                }
                hiddenReasonKey={hiddenReasonKey(excluded.map((cloth) => cloth.reasonCode))}
                offeredTotal={current.offeredTotal}
                hiddenTotal={current.hiddenTotal}
              />
              <BookPager page={current} onPage={turnPage} />
            </>
          )}
        </>
      )}
    </>
  );

  if (inFlow) return body;

  return (
    <AppShell
      title={t('book.heading')}
      ground="book"
      banner={book.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {body}
    </AppShell>
  );
}

function hiddenReasonKey(codes: readonly (string | null)[]): CopyKey {
  const unique = new Set(codes.filter((code): code is string => code != null));
  if (unique.size !== 1) return 'book.tier0.reason.mixed';
  const only = [...unique][0];
  if (only === 'P-FAB-03') return 'book.tier0.reason.notBlackTie';
  if (only === 'P-FAB-04') return 'book.tier0.reason.wrongGarment';
  if (only === 'FABRIC_INSUFFICIENT_STOCK') return 'book.tier0.reason.notEnough';
  return 'book.tier0.reason.mixed';
}

function FilterGroup({
  legend,
  values,
  labelOf,
  chosen,
  onChoose,
}: {
  legend: string;
  values: readonly string[];
  labelOf: (value: string) => string;
  chosen: string;
  onChoose: (value: string) => void;
}) {
  // Facet values render ONLY when present in the current result set.
  if (values.length === 0) return null;
  return (
    <fieldset className="pt-filter">
      <legend className="pt-filter__legend">{legend}</legend>
      {/* A row of options, not a column (the C6 construction): five columns
          of radio options were the whole first viewport. */}
      <RovingRadioGroup
        ariaLabel={legend}
        values={values}
        chosen={chosen}
        labelOf={labelOf}
        onChoose={onChoose}
        toggle
        rowClassName="pt-filter__values pt-filter__values--row"
      />
    </fieldset>
  );
}

/* ------------------------------------------------------------- SCR-CLOTH */

export function ClothScreen({ clothId, draftId }: { clothId: string; draftId?: string }) {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const submission = useSubmission();
  const [interestRecorded, setInterestRecorded] = useState(false);
  const cloth = useResource(() => api.fabric(clothId, draftId === undefined ? {} : { draftId }), [
    clothId,
    draftId,
  ]);

  const data = cloth.data;
  /* C1, 5 September 2026. This screen is given no draft and so has no
     commission to name: both routes that render it pass only the cloth. Until
     then the label slot was filled with `review.cloth.heading` — the words
     "The cloth" — which would have read "your The cloth needs 8 m" the day a
     draft was wired through. With no label the availability line and the
     not-selectable statement use their out-of-context sentences, which are
     whole. When this screen is given a draft, resolve the label from its
     `commission.type` through `commissionTypeLabel()` and pass it here. */
  const typeLabel: string | null = null;
  const reason = data === null ? null : clothReasonText(data);

  return (
    <AppShell
      title={data?.name ?? t('book.heading')}
      ground="book"
      banner={cloth.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {cloth.phase === 'skeleton' || cloth.phase === 'idle' ? (
        <LoadingRegion rows={4} variant="tile" slow={cloth.slow} label={t('book.heading')} />
      ) : null}
      {cloth.phase === 'error' ? <FetchError whatKey="cloth.error.what" onRetry={cloth.reload} /> : null}

      {data === null ? null : (
        <>
          <MacroSwatch cloth={data} />
          <FactLine>
            {t('cloth.facts', {
              composition: compositionLine(data.composition),
              weightGsm: data.weight_gsm,
              weaveLabel: weaveLabel(data.weave) ?? '',
              widthCm: data.width_cm,
            })}
          </FactLine>
          {/* C1, 5 September 2026: the mill line renders only for a NAMED mill.
              A placeholder cloth is shown without one, never as "Woven by
              House selection (placeholder)". */}
          {data.mill === null ? null : <FactLine>{t('cloth.mill', { millName: data.mill })}</FactLine>}

          <ColourStatement
            clothName={data.name}
            pattern={data.pattern}
            patternRepeatCm={data.patternRepeatCm}
            swatchHref={`/book/${data.id}/swatch`}
            onRequestSwatch={() => navigate(`/book/${data.id}/swatch`)}
            asAction
          />

          <AvailabilityLine
            availableMetres={data.availableMetres}
            requirementMetres={data.clothRequirementMetres}
            commissionTypeLabel={typeLabel}
            retired={data.lifecycleState === 'discontinued'}
          />

          <FactLine emphasis>
            {data.totalPriceForCommissionMinor == null
              ? t('cloth.nocontext.priceNote', {
                  pricePerMetreGBP: money(data.pricePerMetreMinor, data.currency),
                })
              : money(data.totalPriceForCommissionMinor, data.currency)}
          </FactLine>

          <section className="pt-block">
            <h2 className="pt-record__groupHeading">{t('cloth.forWhat.heading')}</h2>
            <FactLine>
              {t('book.card.forWhat', {
                occasionList: joinList(data.occasions.map(occasionLabel)),
                garmentList: joinList(data.garments.map((g) => tDynamic(`global.garment.${g}`))),
              })}
            </FactLine>
          </section>

          {/* RULING-007: the house note renders ONLY if authored. */}
          {data.houseNote == null || data.houseNote === '' ? null : (
            <Statement>{data.houseNote}</Statement>
          )}

          {reason === null ? null : <Statement>{reason}</Statement>}

          {/* Two actions, both legible peers. Where the cloth is not selectable
              the commission action is REPLACED BY A STATEMENT naming the
              reason — never a disabled button. */}
          {data.selectable ? (
            <div className="pt-actions pt-actions--equal">
              <Button variant="primary" onClick={() => navigate(`/commission?cloth=${data.id}`)}>
                {t('cloth.action.commission')}
              </Button>
              <Button onClick={() => navigate(`/book/${data.id}/swatch`)}>
                {t('cloth.action.swatch')}
              </Button>
            </div>
          ) : (
            <>
              <Statement>{notSelectableStatement(data, typeLabel)}</Statement>
              <div className="pt-actions">
                <Button onClick={() => navigate(`/book/${data.id}/swatch`)}>
                  {t('cloth.action.swatch')}
                </Button>
              </div>

              {/* The three routes, at EQUAL WEIGHT. Never a silent accept, never
                  a hidden backorder, never a quiet reduction of the commission. */}
              <section className="pt-section">
                <h2 className="pt-record__groupHeading">{t('cloth.routes.heading')}</h2>
                {data.nearestAlternative === null ? null : (
                  <div className="pt-block">
                    <FactLine>
                      {t('cloth.route.nearest', { nearestClothName: data.nearestAlternative.name })}
                    </FactLine>
                    <Button
                      onClick={() =>
                        navigate(`/book/${(data.nearestAlternative as { id: string }).id}`)
                      }
                    >
                      {t('cloth.route.nearest.action', {
                        nearestClothName: data.nearestAlternative.name,
                      })}
                    </Button>
                  </div>
                )}
                {data.millWeavesStill ? (
                  <div className="pt-block">
                    <FactLine>
                      {t('cloth.route.mill', { tailorName: tailor?.displayName ?? '' })}
                    </FactLine>
                  </div>
                ) : null}
                <div className="pt-block">
                  <FactLine>{t('cloth.route.interest')}</FactLine>
                  {interestRecorded ? (
                    <FactLine>
                      {t('cloth.route.interest.recorded', { clothName: data.name })}
                    </FactLine>
                  ) : (
                    <Button
                      busy={submission.busy}
                      busyLabel={t('swatch.busy.action')}
                      onClick={() => {
                        void submission
                          .run(() => api.registerClothInterest({ clothId: data.id }))
                          .then((result) => result && setInterestRecorded(true));
                      }}
                    >
                      {t('cloth.route.interest.action')}
                    </Button>
                  )}
                </div>
              </section>
            </>
          )}

          {data.lifecycleState === 'discontinued' ? (
            <>
              <Statement>{t('cloth.discontinued.statement')}</Statement>
              {data.millWeavesStill ? (
                <Statement>
                  {t('cloth.discontinued.mill', { tailorName: tailor?.displayName ?? '' })}
                </Statement>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </AppShell>
  );
}

/**
 * The sentence that REPLACES the commission action on a cloth that cannot be
 * selected. Always a whole sentence; exported so the test can hold it to that.
 *
 * AMENDED 5 September 2026 (C1). `cloth.short.statement` was being filled with
 * `commissionTypeLabel: ''`, which prints "Your  needs 8 m." — a fragment with
 * a hole where the commission should be. The label is now an argument: with
 * one, the full statement; without one, the shelf figure is stated on its own
 * (`el.availability.outOfContext`), which is true and whole, rather than a
 * sentence with a missing word.
 */
export function notSelectableStatement(
  cloth: {
    reasonCode: string | null;
    name: string;
    availableMetres: number;
    clothRequirementMetres: number | null;
    garments: readonly string[];
  },
  commissionTypeLabel: string | null,
): string {
  switch (cloth.reasonCode) {
    case 'P-FAB-03':
      return t('cloth.notBlackTie.statement');
    case 'P-FAB-04':
      return t('cloth.wrongGarment.statement', {
        garmentLabel: joinList(cloth.garments.map((g) => tDynamic(`global.garment.${g}`))),
      });
    case 'FABRIC_INSUFFICIENT_STOCK':
      if (cloth.availableMetres <= 0) return t('cloth.none.statement');
      if (commissionTypeLabel === null || cloth.clothRequirementMetres === null) {
        return t('el.availability.outOfContext', { availableMetres: metres(cloth.availableMetres) });
      }
      return t('cloth.short.statement', {
        commissionTypeLabel,
        requirementMetres: metres(cloth.clothRequirementMetres),
        availableMetres: metres(cloth.availableMetres),
      });
    default:
      return t('cloth.none.statement');
  }
}

/* ------------------------------------------------------------ SCR-SWATCH */

/**
 * UNCONDITIONAL AND FRICTIONLESS ([T-41]): free, no minimum, no reason asked,
 * no account step, no upsell, no limit stated, no nag. Offered even when it
 * delays or prevents an order — the unconditionality is what makes it a gift
 * rather than a hook.
 *
 * `ST-SWATCH-RECORDED` confirms what a person in the atelier will do and DOES
 * NOT CLAIM A MESSAGE WAS SENT: no transport is wired.
 */
export function SwatchScreen({ clothId }: { clothId: string }) {
  const [address, setAddress] = useState('');
  const [recorded, setRecorded] = useState(false);
  const [existingAt, setExistingAt] = useState<string | null>(null);
  const submission = useSubmission();
  const cloth = useResource(() => api.fabric(clothId), [clothId]);
  const name = cloth.data?.name ?? '';

  return (
    <AppShell title={t('swatch.default.heading', { clothName: name })} width="ledger">
      <Statement>{t('swatch.default.statement')}</Statement>

      {existingAt !== null ? (
        <Statement>
          {t('swatch.existing.statement', {
            clothName: name,
            requestedDate: dateInWords(existingAt),
          })}
        </Statement>
      ) : recorded ? (
        <Statement>{t('swatch.recorded.statement', { clothName: name })}</Statement>
      ) : (
        <>
          {submission.rejection === null ? null : <FetchError whatKey="swatch.error.what" />}
          {/* ST-SWATCH-ADDRESS — exactly one field, never a form. */}
          <TextInput label={t('swatch.address.label')} value={address} onChange={setAddress} />
          <div className="pt-actions">
            <Button
              variant="primary"
              busy={submission.busy}
              busyLabel={t('swatch.busy.action')}
              onClick={() => {
                void submission
                  .run(() => api.requestSwatch({ clothId, address }))
                  .then((result) => {
                    if (result === null) return;
                    if (result.existingAt !== undefined) setExistingAt(result.existingAt);
                    else setRecorded(true);
                  });
              }}
            >
              {t('swatch.default.action')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}
